import { Message } from 'view-design'
import { MessageBox } from 'element-ui'
import { reactive, watch, ref, unref, toRefs, computed, nextTick, set } from 'vue'
import { clone, groupBy } from '@/common/js/just.js'
import { shuffle } from '@/common/js/utils.js'
import { AMEME_TYPE, DELIVERY_MODE } from '../consts/index.js'
import { MATERIAL_TYPE_ID, MATERIAL_TYPE, MATERIAL_TYPE_NAME } from '@/components/material-selector/consts.js'
import { makeSelectorForm } from '@/components/material-selector/index.js'
import { getCreativeFormModel } from '../model/form.js'
import { MEDIAS } from '@/common/js/constants.js'
import { getCreativesByImageCodes, queryImageDetailByIds } from '@/api/common.model'
import { dataFormatter } from '@/components/material-selector/utils.js'
import { getAuthedAwemeList, getAuthedAwemeListByStarDeliveryTaskId } from '@/api/toutiao.model'
import { getAccountById } from '@/api/ptom.data.model.js'
import { IMAGE_TYPES } from '@/views/material/uploading/consts.js'
const getDistributeFormModel = () => {
  return {
    group: 2, // 分配多少个创意组数量
    groupAsSameImageId: false, // 同编号素材优先组合
    [MATERIAL_TYPE.VIDEO]: '', // 每个创意组视频数量
    [MATERIAL_TYPE.IMAGE]: '', // 每个创意组图片数量
    [MATERIAL_TYPE.IMAGE_AUDIO]: '' // 每个创意组图文数量
  }
}

let increasingGroupIndex = 0

/**
 * @description: 图文选择
 */
export const useCarouselSelector = () => {
  const carouselModel = reactive({
    showCarouselAddModal: false
  })

  const onShowCarouselAddModal = () => {
    carouselModel.showCarouselAddModal = true
  }

  return {
    ...toRefs(carouselModel),
    onShowCarouselAddModal
  }
}

export const useAwemeVideoSelector = (configForm, awemeDistributeForm, creativeFormGroups) => {
  const awemeVideoModel = reactive({
    showAwemeVideoSelectorModal: false,
    isAutoDistribute: false
  })

  const onShowAwemeCreativeSelector = (isAutoDistribute = false) => {
    awemeVideoModel.showAwemeVideoSelectorModal = true
    awemeVideoModel.isAutoDistribute = isAutoDistribute
  }

  const onAwemeSelectorConfirm = (list, currCreativeGroupIndex) => {
    if (awemeVideoModel.isAutoDistribute) {
      onAwemeVideoAutoDistribute(list)
    } else {
      const newList = uniqueFilter(list)
      creativeFormGroups.value[currCreativeGroupIndex].selectedMaterials.video = [...newList]
      awemeVideoModel.showAwemeVideoSelectorModal = false
    }
  }
  const onAwemeVideoAutoDistribute = (list) => {
    const { group, video } = awemeDistributeForm.value
    // 没有开启自动优选时只能每个组分配一个
    const videoCount = video || 1
    if (!list || list.length === 0) return Message.error('请选择视频')
    if (!group) return Message.error('请设置创意组数量')
    // 如果未设置单组视频数量，或者数量<=0，视为非法（根据业务逻辑通常需要至少1个）
    if (!videoCount || videoCount <= 0) return Message.error('请设置每个创意组的视频数量')

    const requiredTotal = group * videoCount
    if (list.length < requiredTotal) {
      return Message.error(`所选视频总数(${list.length}) 不足以分配给 ${group} 个组（每组 ${videoCount} 个），需至少 ${requiredTotal} 个视频`)
    }

    const creatives = []

    for (let i = 0; i < group; i++) {
      // 2. 实现新的分配算法：根据 group 参数创建指定数量的组，每组分配 videoCount 个视频
      // 4. 分配完成后需验证：每个组恰好包含 videoCount 个视频，且所有视频不重复分配
      const start = i * videoCount
      const end = start + videoCount
      const groupVideos = list.slice(start, end)

      // 预处理视频信息
      groupVideos.forEach((item) => {
        item.preview_url = item.video_info.video_cover_url
        item.vertical = true
      })

      creatives.push({
        account: configForm.value.accounts[0].id,
        // 自动优选素材时不需设置 dyAccount，显示全部的达人视频
        // 默认使用该组第一个视频的账号作为 dyAccount (逻辑保持与旧版一致或根据需求调整)
        // dyAccount: isStarAutoMaterial.value ? '' : groupVideos[0].aweme_id,
        dyAccount: '',
        creative_name: `创意组${++increasingGroupIndex}`,
        selectedMaterials: {
          video: groupVideos,
          image: [],
          imageAudio: []
        }
      })
    }
    creativeFormGroups.value.push(...creatives)
    awemeVideoModel.showAwemeVideoSelectorModal = false
  }

  function uniqueFilter(list) {
    const existVideo = creativeFormGroups.value[0].selectedMaterials.video
    const existVideoIds = existVideo.map(item => item.video_info.item_id)
    const uniqueItems = list.filter(j => !existVideoIds.includes(j.video_info.item_id))
    return [...existVideo, ...uniqueItems]
  }

  const onAwemeVideoSelectorClose = () => { }

  return {
    ...toRefs(awemeVideoModel),
    onShowAwemeCreativeSelector,
    onAwemeSelectorConfirm,
    onAwemeVideoSelectorClose
  }
}

export const useAwemeVideoCreatives = (configForm) => {
  const model = reactive({
    queryAwemeVideoLoading: false,
    awemeVideoGroupByAccountIdObj: {},
    awemeVideoDataLoaded: false
  })

  /**
   * @description: 账号下的抖音达人视频列表，避免重复请求
   * @param {*} accountId
   * @param {*} project 批量复制、修改时传入，新建时取configForm.value.project
   * @return {*}
   */
  const getAwemeVideoListByAccountId = async (accountId, project = {}) => {
    // if (model.awemeVideoGroupByAccountIdObj[accountId]) return
    try {
      model.queryAwemeVideoLoading = true
      model.awemeVideoDataLoaded = false

      // 第二个参数 project 批量复制、修改时传入，新建时取configForm.value.project，兼容批量复制、修改流程
      const { star_task_id } = project.star_task || configForm.value.project.star_task || {}
      let data = {}

      if (star_task_id) {
        const accountInfo = await getAccountById({ ad_account_id: accountId })
        data = await getAuthedAwemeListByStarDeliveryTaskId({
          advertiserId: accountInfo.advertiserId,
          adAccount: accountInfo.adAccount,
          starTaskId: star_task_id
        })
        // 格式化，与普通达人视频格式保持一致
        const data2 = data ? data.map((i) => {
          return { auth_type: 'VIDEO_ITEM', aweme_id: i.aweme_id, aweme_name: i.aweme_name, video_info: { ...i, title: i.video_title } }
        }) : []
        set(model.awemeVideoGroupByAccountIdObj, accountId, data2)
      } else {
        data = await getAuthedAwemeList({ ad_account_id: accountId })
        set(model.awemeVideoGroupByAccountIdObj, accountId, data)
      }
      model.queryAwemeVideoLoading = false

      return Promise.resolve()
    } catch (e) {
      console.log('getAwemeVideoListByAccountId error output: ', e)
      model.queryAwemeVideoLoading = false
      return Promise.reject(new Error(e))
    }
  }

  return {
    ...toRefs(model),
    getAwemeVideoListByAccountId
  }
}

/**
 * @description: 创意组
 */
export const useCreativeGroups = (configForm) => {
  const groupModel = reactive({
    currCreativeGroupIndex: 0,
    creativeFormGroups: []
  })

  const creativeFormRef = ref(null)

  const currGroup = computed({
    get() {
      return groupModel.creativeFormGroups[groupModel.currCreativeGroupIndex]
    },
    set(g) {
      groupModel.creativeFormGroups.splice(groupModel.currCreativeGroupIndex, 1, g)
    }
  })

  /**
   * @description: 新增一个创意组
   * @return {*}
   */
  const onCreativeGroupAdd = () => {
    increasingGroupIndex++
    const creative_name = `创意组${increasingGroupIndex}`
    const creativeFormItem = getCreativeFormModel()

    // 为图文素材添加默认账号，默认为已选账号的第一个
    // creativeFormItem.imageAudioAccount = getAdAccount()
    groupModel.creativeFormGroups.push({
      ...creativeFormItem,
      creative_name
    })
    groupModel.currCreativeGroupIndex = groupModel.creativeFormGroups.length - 1
  }
  /**
   * @description: 删除一个创意组
   * @param {*} index
   * @return {*}
   */
  const onCreativeGroupItemRemove = (index) => {
    groupModel.creativeFormGroups = groupModel.creativeFormGroups.filter((item, i) => i !== index)
    groupModel.currCreativeGroupIndex = index - 1 < 0 ? 0 : index - 1
  }

  /**
   * @description: 复制一个创意组
   * @return {*}
   */
  const onCreativeGroupDuplicate = (index) => {
    if (groupModel.creativeFormGroups.length >= 200) return

    const dupItem = clone(groupModel.creativeFormGroups[index])
    dupItem.creative_name += '_复制'
    groupModel.creativeFormGroups.splice(index + 1, 0, dupItem)
    groupModel.currCreativeGroupIndex = index + 1
  }

  const onBeforeConfigConfirm = async (done) => {
    let errorIndex = -1
    try {
      for (const index in creativeFormRef.value) {
        const formRef = creativeFormRef.value[index]
        errorIndex = index
        await formRef.validate()
      }

      done()
    } catch (e) {
      console.log('onBeforeConfigConfirm creative validate error output: ', e)
      groupModel.currCreativeGroupIndex = Number(errorIndex)

      if (configForm.value.plan.awemeIdentity === AMEME_TYPE.AWEME_VIDEO) {
        Message.warning(e.message || '请选择达人视频')
      } else {
        if (typeof e === 'object') {
          const [error] = Object.values(e) || []
          const [errorMsg] = error || []

          // 切换对应的素材类型tab
          if (errorMsg.message.startsWith('图片')) {
            groupModel.creativeFormGroups[groupModel.currCreativeGroupIndex].materialType = MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE]
          }
          if (errorMsg.message.startsWith('视频')) {
            groupModel.creativeFormGroups[groupModel.currCreativeGroupIndex].materialType = MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO]
          }
        }
        onUpdateCreativeForm(groupModel.creativeFormGroups[groupModel.currCreativeGroupIndex])
      }
    }
  }

  /**
   * @description: 全量更新当前创意组
   * @param {*} form
   */
  const onUpdateCreativeForm = (form) => {
    if (Array.isArray(creativeFormRef.value)) {
      // 在模板新建时，v-for多个创意组，creativeFormRef.value是数组，需要指定修改的索引
      // 查看src/views/create/bytedance/components/creative/index.vue creativeFormRef
      creativeFormRef.value[groupModel.currCreativeGroupIndex].setForm(form)
    }
    // else {
    //   // 批量复制、修改时，creativeFormRef.value 只对单个创意组修改
    //   // 查看 src/views/create/bytedance/modify.vue creativeFormRef
    //   creativeFormRef.value.setForm(form)
    // }
  }

  return {
    ...toRefs(groupModel),
    creativeFormRef,
    currGroup,
    onUpdateCreativeForm,
    onBeforeConfigConfirm,
    onCreativeGroupAdd,
    onCreativeGroupItemRemove,
    onCreativeGroupDuplicate
  }
}

/**
 * @description: 创意素材弹窗
 * @param {*} getAdAccount 获取当前已选账号的第一个账号
 * @param {*} currGroup 当前创意组
 * @param {*} creativeFormGroups 创意组
 * @param {*} onUpdateCreativeForm 更新当前创意组
 * @return {*}
 */
export const useCreativeSelector = (getAdAccount, currGroup, creativeFormGroups, onUpdateCreativeForm, configForm, updateConfigForm) => {
  const selectorMaxCount = computed(() => {
    if (configForm.value.isV3) return 800

    const { delivery_mode } = configForm.value.project
    // 图文选择素材时最大10个
    if (currGroup.value?.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO]) {
      return 10
    }

    // 非图文素材且是自动投放时，选择素材时最大30个
    if (delivery_mode === DELIVERY_MODE.PROCEDURAL) {
      return 30
    }

    return 10
    // return currGroup.value.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO] ? imageAudioSelectorMaxCount : normalSelectorMaxCount
  })
  const selectorModel = reactive({
    // onSelectorClose 会重置此属性
    isDistributionMode: false,
    // 创意组分配
    distributeForm: getDistributeFormModel(),
    // 重新打乱创意组分配
    recombineDistrubuteForm: getDistributeFormModel(),
    // 达人视频创意组配分配
    awemeDistributeForm: { group: 2, video: 1 },
    showSelectorModal: false,
    recombineLoading: false,

    // 保存替换素材时的辅助参数，onSelectorClose 会重置对象内属性
    replaceProps: {
      materialType: null,
      materialTabIndex: null
    },
    selectorProps: {
      adAccountId: 0,
      max: selectorMaxCount.value,
      videoId: 0, // 获取视频封面对应的视频id，替换已选视频封面时传入
      assetType: MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO], // 1单图 2双图 3三图 4视频 6头条视频 7头条图片 8LOGO
      form: {}
    }
  })

  // 监听配置变化，更新一键打乱时素材个数配置，
  // 只会在src/views/create/bytedance/group.vue onTemplateConfigConfirm 切换模板时触发
  watch(
    () => configForm.value.recombineHelpers.distributeForm,
    (val) => {
      if (val?.group) {
        selectorModel.recombineDistrubuteForm = { ...getDistributeFormModel(), ...val }
      }
    }
  )

  const seletedMaterialLength = computed(() => {
    const { selectedMaterials } = currGroup.value

    return {
      [MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE]]: selectedMaterials.image?.length || 0,
      [MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO]]: selectedMaterials.video?.length || 0,
      [MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO]]: selectedMaterials.imageAudio?.length || 0
    }
  })
  const selectedMaterialTotal = computed(() => Object.values(seletedMaterialLength.value).reduce((total, length) => total + length, 0))
  const limitSelectedMaterials = ({ video, image, imageAudio }) => {
    if (!configForm.value.isV3) {
      return {
        video: video.slice(0, selectorMaxCount.value),
        image: image.slice(0, selectorMaxCount.value),
        imageAudio: imageAudio.slice(0, selectorMaxCount.value)
      }
    }

    const materialLimit = 800
    const selected = [
      ...video.map((item) => ({ type: 'video', item })),
      ...image.map((item) => ({ type: 'image', item })),
      ...imageAudio.map((item) => ({ type: 'imageAudio', item }))
    ].slice(0, materialLimit)

    return selected.reduce(
      (materials, { type, item }) => {
        materials[type].push(item)
        return materials
      },
      { video: [], image: [], imageAudio: [] }
    )
  }
  // 素材类型是否禁用逻辑
  const seletedMaterialRadioDiasbled = computed(() => {
    const { isDistributionMode, replaceProps } = selectorModel
    // 是否素材替换
    const isReplaceMode = !!replaceProps.materialType
    // 是否正常选择素材
    const isNormalMode = !isReplaceMode

    const states = {
      [MATERIAL_TYPE.IMAGE]: true,
      [MATERIAL_TYPE.VIDEO]: true,
      [MATERIAL_TYPE.LOGO]: true,
      [MATERIAL_TYPE.POSTER]: true,
      [MATERIAL_TYPE.IMAGE_AUDIO]: true
    }

    // 正常选择素材时、批量添加创意组可以切换视频、图片、图文、收藏
    if (isNormalMode || isDistributionMode) {
      states[MATERIAL_TYPE.IMAGE] = false
      states[MATERIAL_TYPE.VIDEO] = false
      states[MATERIAL_TYPE.IMAGE_AUDIO] = false
    }

    // 替换时，只能选择对应的素材类型
    if (isReplaceMode) {
      states[replaceProps.materialType] = false

      // 如果替换类型为封面，可以将图片radio放开，让用户可以选择图片作为封面
      if (replaceProps.materialType === MATERIAL_TYPE.POSTER) {
        states[MATERIAL_TYPE.IMAGE] = false
      }
    }

    return states
  })

  /**
   * @description: 替换已选素材
   * @param {*} materialType 需替换的素材类型
   * @param {*} materialTabIndex 需替换的素材tab所在的索引
   */
  const onShowRepalceMaterialSelector = (materialTabIndex, materialType) => {
    selectorModel.selectorProps = {
      ...selectorModel.selectorProps,
      max: 1,
      videoId: 0,
      assetType: MATERIAL_TYPE_ID[materialType],
      form: { ...makeSelectorForm(), size: '', media_id: MEDIAS.TOUTIAO }
    }

    selectorModel.replaceProps.materialType = materialType
    selectorModel.replaceProps.materialTabIndex = materialTabIndex

    // 替换封面
    if (materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.POSTER]) {
      const { id: videoId } = currGroup.value.selectedMaterials.video[materialTabIndex]
      // 替换封面时，需要传入对应的视频id
      selectorModel.selectorProps.videoId = videoId
    }

    selectorModel.showSelectorModal = true
  }

  /**
   * @description: 显示素材选择弹窗
   * @param {isDistribute} 是否是创意组智能分配
   * @return {*}
   */
  const onShowCreativeSelector = (isDistributionMode) => {
    const { materialType = MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO] } = currGroup.value || {}

    // 初始化累计起始值
    increasingGroupIndex = Math.max(increasingGroupIndex, creativeFormGroups.value.length)

    // 最大可选数量：isDistributionMode 批量新建创意组时不限；常规选择素材时最大30个，且需要减去已选素材数量
    const max = isDistributionMode
      ? -1
      : configForm.value.isV3
        ? Math.max(0, selectorMaxCount.value - selectedMaterialTotal.value)
        : selectorMaxCount.value - seletedMaterialLength.value[materialType]
    selectorModel.selectorProps = {
      ...selectorModel.selectorProps,
      // 图文账号ID，使用当前创意组图文tab选择的账号进行查询
      adAccountId: getAdAccount().id,
      max,
      assetType: materialType,
      form: { ...makeSelectorForm(), sizes: ['1280*720', '720*1280', '1920*1080', '1080*1920'], media_id: MEDIAS.TOUTIAO }
    }
    selectorModel.isDistributionMode = isDistributionMode
    // 如果是智能分配模式，每次打开弹窗都重置分配配置
    if (isDistributionMode) {
      resetDistributeForm()
    }
    selectorModel.showSelectorModal = true
  }
  /**
 * 核心分配引擎 (distributeItemsRandom)
  */
  function distributeItemsRandom(data, options = {}) {
    const {
      numArrays = 1,
      targetPerArray = 0,
      shuffleGroups = true,
      shuffleItems = true
    } = options

    const result = Array.from({ length: numArrays }, () => [])
    let groupIds = Object.keys(data)
    if (shuffleGroups) groupIds = shuffle(groupIds)

    for (const groupId of groupIds) {
      let items = data[groupId]
      if (shuffleItems) items = shuffle(items)

      const itemsWithParent = items.map(item => ({ ...item, _groupId: groupId }))
      let placed = false

      // 尝试整体放入
      for (let i = 0; i < numArrays; i++) {
        if (result[i].length + itemsWithParent.length <= targetPerArray) {
          result[i].push(...itemsWithParent)
          placed = true
          break
        }
      }

      // 无法整体放入则拆分填充
      if (!placed) {
        const remainingItems = [...itemsWithParent]
        for (let i = 0; i < numArrays && remainingItems.length > 0; i++) {
          const available = targetPerArray - result[i].length
          if (available > 0) {
            result[i].push(...remainingItems.splice(0, available))
          }
        }
      }
    }
    return result
  }

  function distributeMaterialsSecure(materials, distributeForm) {
    const { group, groupAsSameImageId = false, ...restDistributeForm } = distributeForm
    const numGroups = group || 0
    if (numGroups <= 0) throw new Error('分配[创意组]必须大于 0')
    if (!restDistributeForm.video && !restDistributeForm.image && !restDistributeForm.image_audio) throw new Error('请至少输入一个素材类型的分配个数')

    const resourceTypes = Object.keys(restDistributeForm)
    const errors = []
    const warnings = []

    // --- 1. 预检：数量校验 ---
    resourceTypes.forEach(type => {
      const requiredEach = restDistributeForm[type] || 0
      const totalRequired = requiredEach * numGroups
      const totalAvailable = materials[type]?.length || 0

      if (totalAvailable < totalRequired) {
        const missing = totalRequired - totalAvailable
        errors.push(`[${MATERIAL_TYPE_NAME[type]}] 数量不足：需要${totalRequired}，实际${totalAvailable}，缺 ${missing}个；`)
      }
    })

    // 如果有任何资源不足，抛出异常或根据业务逻辑决定是否继续
    if (errors.length > 0) {
      // console.error('❌ 分配失败，资源库存不足：\n' + errors.join('\n'))
      // 这里可以选择 throw Error，或者只打印警告继续分配能分配的部分
      throw new Error('分配失败，选择素材不足：\n' + errors.join('\n'))
    }

    // --- 2. 准备数据结构 ---

    // 视频：按 imageId 聚合
    const videoData = {}
    if (groupAsSameImageId) {
      (materials.video || []).forEach(v => {
        if (!videoData[v.imageId]) videoData[v.imageId] = []
        videoData[v.imageId].push(v)
      })
    } else {
      // 视频：按 ID 独立
      (materials.video || []).forEach(v => { videoData[v.id] = [v] })
    }
    // 图片：按 ID 独立（模拟 distributeItemsRandom 需要的键值对结构）
    const imageData = {};
    (materials.image || []).forEach(img => { imageData[img.id] = [img] })

    // 图文：按 ID 独立
    const imageAudioData = {};
    (materials.image_audio || []).forEach(gi => { imageAudioData[gi.id] = [gi] })

    // --- 3. 调用引擎分配 ---
    const videoResults = distributeItemsRandom(videoData, {
      numArrays: numGroups,
      targetPerArray: restDistributeForm.video
    })

    const imageResults = distributeItemsRandom(imageData, {
      numArrays: numGroups,
      targetPerArray: restDistributeForm.image
    })

    const imageAudioResults = distributeItemsRandom(imageAudioData, {
      numArrays: numGroups,
      targetPerArray: restDistributeForm.image_audio
    })

    // --- 4. 合并并进行次级预警（针对视频 imageId 拆分情况） ---
    const finalResult = videoResults.map((vArr, index) => {
      const iArr = imageResults[index]
      const iAArr = imageAudioResults[index]

      // 检查该组是否达到了预期的数量
      if (vArr.length < restDistributeForm.video) {
        warnings.push(`第 ${index + 1} 组视频数量未达标: 期望 ${restDistributeForm.video}, 实际 ${vArr.length}`)
      }

      const combined = [...vArr, ...iArr, ...iAArr]
      // return shuffle(combined) // 最终组内顺序随机
      return combined
    })

    if (warnings.length > 0) {
      console.warn('分配完成，但存在以下异常：\n' + warnings.join('\n'))
    }
    return finalResult
  }

  /**
   * @description: 素材选择确认前置处理、校验，如果通过校验，生成创意组
   * @param {*} done
   * @param {*} fail
   */
  const onSelectorBeforeSubmit = (seletedMaterials, callbacks) => {
    const { done, fail } = callbacks || {}
    const { distributeForm } = selectorModel

    const video = seletedMaterials.filter((item) => item.type === MATERIAL_TYPE.VIDEO)
    const image = seletedMaterials.filter((item) => item.type === MATERIAL_TYPE.IMAGE)
    const image_audio = seletedMaterials.filter((item) => item.type === MATERIAL_TYPE.IMAGE_AUDIO)

    // 重置替换素材辅助参数
    const materials = {
      [MATERIAL_TYPE.VIDEO]: video,
      [MATERIAL_TYPE.IMAGE]: image,
      [MATERIAL_TYPE.IMAGE_AUDIO]: image_audio
    }

    try {
      const results = distributeMaterialsSecure(materials, distributeForm)
      done && done()

      return results
    } catch (e) {
      return Message.error({ duration: 8, content: e.message })
    }
  }

  function resetDistributeForm() {
    selectorModel.distributeForm = { ...getDistributeFormModel() }
  }

  /**
   * @description: 批量生成创意组
   * @param {*} e
   * @return {*}
   */
  const getAutoDistributeGroups = (e) => {
    const res = onSelectorBeforeSubmit(e)
    if (!res || typeof res !== 'object') return

    const newGroups = res.map(item => {
      const creativeFormItem = getCreativeFormModel()

      for (let i = 0; i < item.length; i++) {
        const asset = item[i]
        if (asset.type === 'image_audio') {
          creativeFormItem.selectedMaterials.imageAudio.push({ ...asset, imageAudioAccount: { id: selectorModel.selectorProps.adAccountId } })
        } else {
          creativeFormItem.selectedMaterials[asset.type].push(asset)
        }
      }

      creativeFormItem.selectedMaterials = limitSelectedMaterials(creativeFormItem.selectedMaterials)

      return creativeFormItem
    })

    return newGroups
  }

  /**
   * @description: 素材选择确认，并生成创意组
   * @param {*} e
   * @return {*}
   */
  const onSelectorConfirm = (e) => {
    const { isDistributionMode, replaceProps } = selectorModel

    // 素材替换处理
    if (replaceProps.materialType) {
      const { materialType, materialTabIndex } = replaceProps
      if (materialType === MATERIAL_TYPE.POSTER) {
        const [poster] = e
        const curr = unref(currGroup)

        curr.selectedMaterials.video = curr.selectedMaterials.video.map((item, index) => {
          if (index === materialTabIndex) {
            return {
              ...item,
              cover_id: poster.id,
              cover_height: poster.height,
              cover_width: poster.width,
              cover_url: poster.preview_url,
              cover_size: poster.size,
              cover_replaced_type: poster.type
            }
          }
          return item
        })

        onUpdateCreativeForm(curr)
      }
    } else {
      // 正常选择素材处理

      // 处理智能批量添加创意组
      if (isDistributionMode) {
        const newGroups = getAutoDistributeGroups(e)
        if (newGroups?.length) {
          //  更新创意组
          creativeFormGroups.value = [
            ...creativeFormGroups.value,
            ...newGroups.map((group) => {
              increasingGroupIndex++
              return { ...group, creative_name: `创意组${increasingGroupIndex}` }
            })
          ].slice(0, 200)

          // 图文素材不能通过id从接口获取，作单独的全量数据保存
          const imageAudioList = e.filter(i => i.type === 'image_audio')
          const distributeOriginIds = e.filter(i => i.type !== 'image_audio').map(image => image.id)

          // 更新全局计划配置
          updateConfigForm({
            ...configForm.value,
            recombineHelpers: {
              distributeForm: clone(selectorModel.distributeForm),
              distributeOriginIds,
              distributeImageAudioList: imageAudioList
            }
          })

          // 一键打乱始终使用最后一次智能分配的配置信息
          selectorModel.recombineDistrubuteForm = clone(selectorModel.distributeForm)
        }
      } else {
        // 处理常规手动添加素材
        const selectedMaterials = {
          ...currGroup.value.selectedMaterials
        }

        const videos = selectedMaterials.video
        const images = selectedMaterials.image
        const imageAudios = selectedMaterials.imageAudio

        for (const material of e) {
          // 选择视频
          if (material.type === MATERIAL_TYPE.VIDEO) {
            videos.unshift(material)
          }
          // 选择图片
          if (material.type === MATERIAL_TYPE.IMAGE) {
            images.unshift(material)
          }
          // 选择图文
          if (material.type === MATERIAL_TYPE.IMAGE_AUDIO) {
            // 把图文素材的账号信息带入，用于跨账号图文素材推送
            imageAudios.unshift({ ...material, imageAudioAccount: { id: selectorModel.selectorProps.adAccountId } })
          }
        }

        currGroup.value = {
          ...currGroup.value,
          selectedMaterials: limitSelectedMaterials({ video: videos, image: images, imageAudio: imageAudios })
        }

        nextTick(() => {
          // 创意表单传入的form不是响应式的，需要手动更新
          onUpdateCreativeForm(currGroup.value)
        })
      }
    }
  }

  const onSelectorClose = () => {
    selectorModel.showSelectorModal = false

    // 重置替换素材辅助参数
    selectorModel.replaceProps = {
      materialType: null,
      materialTabIndex: null
    }

    selectorModel.isDistributionMode = false
  }

  /**
   * @description: 重新打乱创意组
   * @return {*}
   */
  const onCreativeGroupsReCombine = async () => {
    const { creatives, recombineHelpers: helper } = configForm.value

    if (selectorModel.recombineLoading) return

    selectorModel.recombineLoading = true

    // 重新打乱的创意组个数等于已有创意组的个数
    selectorModel.distributeForm = {
      ...selectorModel.recombineDistrubuteForm,
      group: creatives.length
    }

    try {
      const imageDetails = helper.distributeOriginIds ? await queryImageDetailByIds({ imageDetailIds: helper.distributeOriginIds }) : []
      if (!imageDetails?.length && !helper.distributeImageAudioList?.length) { selectorModel.recombineLoading = false; return }
      const existCreatives = imageDetails.map(image => {
        const { imageType } = image.items[0]
        if (imageType === IMAGE_TYPES.VIDEO) return dataFormatter.video(image.items[0])
        if (imageType === IMAGE_TYPES.IMAGE) return dataFormatter.image(image.items[0])
      })

      // 把图文数据放到整个数据中
      const newCreativeGroups = getAutoDistributeGroups([...existCreatives].concat(helper.distributeImageAudioList || []))
      const recombineHelpers = {
        ...helper,
        distributeForm: clone(selectorModel.distributeForm)
      }
      updateConfigForm({
        ...configForm.value,
        recombineHelpers,
        creatives: newCreativeGroups.map((group, index) => {
          // 沿用之前的创意组名称
          return { ...group, creative_name: configForm.value.creatives[index].creative_name }
        })
      })

      setTimeout(() => {
        selectorModel.recombineLoading = false
      }, 500)
    } catch (e) {
      console.log('creative recombine error output: ', e)
      selectorModel.recombineLoading = false
    }
  }
  return {
    ...toRefs(selectorModel),
    seletedMaterialRadioDiasbled,
    onShowCreativeSelector,
    onShowRepalceMaterialSelector,
    onSelectorBeforeSubmit,
    getAutoDistributeGroups,
    onSelectorClose,
    onSelectorConfirm,
    onCreativeGroupsReCombine
  }
}

/**
 * @description: 批量导入素材
 * @return {*}
 */
export const useCreativeImportModal = (configForm) => {
  const assetsSelectorRef = ref(null)
  const creativeImportModel = reactive({
    showImportModal: false
  })
  const onShowImportModal = () => {
    creativeImportModel.showImportModal = true
  }

  const onCreativeImportConfirm = async (vals) => {
    try {
      const { list, msg } = await getCreativesByImageCodes({
        imageNames: vals,
        select_game_id: configForm.value.project.game.id,
        mediaId: MEDIAS.TOUTIAO
      })
      const materialTypes = ['', 'image', 'video']

      if (list) {
        const rows = list.map((i) => ({ ...i.items[0], isFavor: i.isFavor })).map((i) => dataFormatter[materialTypes[i.imageType]](i))
        assetsSelectorRef.value.setSelectedRows(rows)
        MessageBox.alert(msg, '批量导入结果', {
          confirmButtonText: '知道了',
          callback: (action) => { }
        })
      }
    } catch (e) {
      console.log('eeeeeeeeeee output: ', e)
    }
  }

  return {
    ...toRefs(creativeImportModel),
    assetsSelectorRef,
    onShowImportModal,
    onCreativeImportConfirm
  }
}
